import React, { useState } from "react";
import styled from "styled-components";
import { colors2, LoaderCircleV2, ButtonPrimaryCompact, ButtonGhostCompact } from "oolib";
import { UI_BODY, UI_BODY_SM, UI_BODY_SEMIBOLD, UI_CAPTION, UI_CAPTION_DF, UI_TAG_DF, UI_TITLE } from "oolib/dist/v2/components/Typo2";
import { useGetInsightsAgentRun, useRunInsightsAgent } from "@/utils/react-query-hooks/aiAnnotation";
import { usePeriodSelection } from "@/components/analysisModules/TopicClusteringTreeViewV2/periodSelection";
import { periodKey } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/periodKeys";
import { PeriodSelector } from "@/components/analysisModules/TopicClusteringTreeViewV2/PeriodPicker";

/**
 * Insights — what the insights agent wrote for the selected period: the
 * insights its desk kept, each with the reporter's body and its sources
 * (the numbers used, the records quoted). TEMPORARY surface: the run is
 * started from here and executes inside the request (local only); the real
 * surface and the hosted run come later.
 */
const Page = styled.div`
  max-width: 880px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 0 40px;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;
const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  padding: 18px 20px;
  border-radius: 12px;
  background: ${colors2.white};
  border: 1px solid ${colors2.grey10};
`;
const Quote = styled.div`
  padding: 8px 12px;
  border-left: 3px solid ${colors2.grey20};
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const Row = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: baseline;
`;

const Insight = ({ insight }) => {
  const [open, setOpen] = useState(false);
  const { sources } = insight;
  return (
    <Card>
      <Row>
        <UI_TAG_DF style={{ color: colors2.grey60 }}>score {insight.score} · confidence {Math.round((insight.confidence || 0) * 100)}%</UI_TAG_DF>
        {insight.verdict !== "insight" && <UI_TAG_DF style={{ color: colors2.grey50 }}>{insight.verdict}</UI_TAG_DF>}
      </Row>
      <UI_TITLE style={{ color: colors2.grey80 }}>{insight.title}</UI_TITLE>
      <UI_BODY_SEMIBOLD style={{ color: colors2.grey70 }}>{insight.description}</UI_BODY_SEMIBOLD>
      <UI_BODY style={{ color: colors2.grey70 }}>{insight.body}</UI_BODY>
      {insight.counterEvidence && (
        <UI_BODY_SM style={{ color: colors2.grey60 }}>
          <span style={{ color: colors2.grey50 }}>But: </span>
          {insight.counterEvidence}
        </UI_BODY_SM>
      )}
      <ButtonGhostCompact S value={open ? "Hide sources" : `Sources · ${sources.quotes.length} quotes · ${sources.numbers.length} numbers`} onClick={() => setOpen((v) => !v)} />
      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
          {insight.factCheck && (
            <div>
              <UI_CAPTION_DF style={{ color: colors2.grey50, display: "block", marginBottom: 4 }}>
                Fact-check · {insight.factCheck.unsupported} of {insight.factCheck.of} sentences not carried by what was read
              </UI_CAPTION_DF>
              {insight.factCheck.sentences.map((s, i) => (
                <UI_BODY_SM key={i} style={{ color: colors2.grey70 }}>
                  “{s.text}” {s.note && <span style={{ color: colors2.grey40 }}>— {s.note}</span>}
                </UI_BODY_SM>
              ))}
            </div>
          )}
          <div>
            <UI_CAPTION_DF style={{ color: colors2.grey50, display: "block", marginBottom: 4 }}>Leads this came from</UI_CAPTION_DF>
            {sources.leads.map((l) => (
              <UI_BODY_SM key={l.id} style={{ color: colors2.grey70 }}>
                {l.kindLabel}: {l.claim}
              </UI_BODY_SM>
            ))}
          </div>
          <div>
            <UI_CAPTION_DF style={{ color: colors2.grey50, display: "block", marginBottom: 4 }}>Numbers used</UI_CAPTION_DF>
            {sources.numbers.map((n, i) => (
              <UI_BODY_SM key={i} style={{ color: colors2.grey70 }}>
                {n.label}: {String(n.value)} <span style={{ color: colors2.grey40 }}>({n.source})</span>
              </UI_BODY_SM>
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <UI_CAPTION_DF style={{ color: colors2.grey50 }}>What customers said · {sources.recordsRead} records read</UI_CAPTION_DF>
            {sources.quotes.map((q) => (
              <Quote key={q.id}>
                <UI_BODY_SM style={{ color: colors2.grey80 }}>“{q.quote}”</UI_BODY_SM>
                <UI_CAPTION_DF style={{ color: colors2.grey50 }}>
                  {[q.dataset, q.date, q.topic, q.subtopic, q.document?.locality, q.inPeriod === false ? "earlier period, context" : null].filter(Boolean).join(" · ")}
                </UI_CAPTION_DF>
              </Quote>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export const InsightsTab = () => {
  const periodSelection = usePeriodSelection();
  const { period } = periodSelection;
  const key = periodKey(period);
  const { data, isLoading, error } = useGetInsightsAgentRun({ period: key });
  const run = useRunInsightsAgent();
  const latest = run.data && run.data.period?.key === key ? run.data : data?.run;
  const stale = run.data && run.data.period?.key === key ? false : !!data?.stale;

  return (
    <Page>
      <Header>
        <PeriodSelector id="insights" inline selection={periodSelection} />
        <Row>
          {latest && (
            <UI_CAPTION style={{ color: colors2.grey50 }}>
              {latest.threads} threads · {(latest.insights || []).length} insights · {Math.round((latest.usage?.ms || 0) / 1000)} s
              {stale ? " · data has changed since" : ""}
            </UI_CAPTION>
          )}
          <ButtonPrimaryCompact S value={run.isLoading ? "Working… (a few minutes)" : latest ? "Run again" : "Run the desk"} disabled={run.isLoading || !key} onClick={() => run.mutate({ period: key, force: !!latest })} />
        </Row>
      </Header>
      {run.isError && <UI_BODY_SM style={{ color: colors2.grey60 }}>The run failed: {run.error?.response?.data?.message || run.error?.message}</UI_BODY_SM>}
      {!key && <UI_BODY_SM style={{ color: colors2.grey60 }}>Pick a month or a year; the desk reads one period at a time.</UI_BODY_SM>}
      {isLoading && <LoaderCircleV2 />}
      {error && <UI_BODY_SM style={{ color: colors2.grey60 }}>Could not load the insights.</UI_BODY_SM>}
      {key && !isLoading && !latest && !run.isLoading && <UI_BODY style={{ color: colors2.grey60 }}>No insights written for {period?.display} yet.</UI_BODY>}
      {latest && !(latest.insights || []).length && <UI_BODY style={{ color: colors2.grey60 }}>The desk found nothing worth an insight in {period?.display}.</UI_BODY>}
      {(latest?.insights || []).map((s) => (
        <Insight key={s.id} insight={s} />
      ))}
    </Page>
  );
};
